import { Suspense } from "react";

import { useSuspenseQuery } from "@tanstack/react-query";
import { IntlProvider, ReactIntlErrorCode } from "react-intl";

import { isChunkError } from "@/shared/misc/chunkReload";
import { logger } from "@/shared/services/SentryLogger.service";

import { LANGUAGE_ID } from "../../lang/lang.constants";

import { CfLoader } from "../common/CfLoader/CfLoader";

type DynamicIntlProviderProps = {
  children: React.ReactNode;
  locale: LANGUAGE_ID;
};

const DynamicIntlProvider = ({
  children,
  locale,
}: DynamicIntlProviderProps): React.ReactElement => (
  <Suspense fallback={<CfLoader />}>
    <TranslationsIntlProvider locale={locale}>
      {children}
    </TranslationsIntlProvider>
  </Suspense>
);

const TranslationsIntlProvider = ({
  children,
  locale,
}: DynamicIntlProviderProps): React.ReactElement => {
  const { data: translations } = useSuspenseQuery({
    queryKey: ["translations", locale],
    queryFn: ({ signal }) => loadTranslations(locale, signal),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: (failureCount, err) => !isChunkError(err) && failureCount < 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  return (
    <IntlProvider
      locale={locale}
      messages={translations}
      onError={(err) => {
        if (err.code === ReactIntlErrorCode.MISSING_TRANSLATION) return;
        logger.error(err);
      }}
    >
      {children}
    </IntlProvider>
  );
};

const loadTranslations = async (
  langId: LANGUAGE_ID,
  signal: AbortSignal,
): Promise<Record<string, string>> => {
  if (signal.aborted) {
    throw new Error("Request was aborted");
  }

  const module = await import(`../../lang/${langId}.json`);
  return module.default || module;
};

export { DynamicIntlProvider };

export default DynamicIntlProvider;
